import React, {Component} from 'react';
import ResizeObserver from 'rc-resize-observer';
import omit from 'omit.js';
import cx from 'classnames';
import calculateNodeHeight from './calculateNodeHeight';
import raf from '../../util/raf';

const RESIZE_STATUS_NONE = 0;
const RESIZE_STATUS_RESIZING = 1;
const RESIZE_STATUS_RESIZED = 2;

class ResizableTextArea extends Component {
  nextFrameActionId = null;

  resizeFrameId = null;

  textArea = null;

  constructor(props: TextAreaProps) {
    super(props);

    this.state = {
      textareaStyles : {},
      resizeStatus   : RESIZE_STATUS_NONE
    };
  }

  saveTextArea = textArea => {
    this.textArea = textArea;
  };

  componentDidMount() {
    this.resizeTextarea();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.resizeTextarea();
    }
  }

  handleResize = size => {
    const {resizeStatus} = this.state;
    const {autoSize, onResize} = this.props;

    if (resizeStatus !== RESIZE_STATUS_NONE) {
      return;
    }

    if (typeof onResize === 'function') {
      onResize(size);
    }

    if (autoSize) {
      this.resizeOnNextFrame();
    }
  };

  resizeOnNextFrame = () => {
    raf.cancel(this.nextFrameActionId);

    this.nextFrameActionId = raf(this.resizeTextarea);
  };

  resizeTextarea = () => {
    const {autoSize} = this.props;

    if (!autoSize || !this.textArea) {
      return;
    }

    const {minRows, maxRows} = autoSize;
    const textareaStyles = calculateNodeHeight(this.textArea, false, minRows, maxRows);

    this.setState({
      textareaStyles,
      resizeStatus : RESIZE_STATUS_RESIZING
    }, () => {
      raf.cancel(this.resizeFrameId);

      this.resizeFrameId = raf(() => {
        this.setState({
          resizeStatus : RESIZE_STATUS_RESIZED
        }, () => {
          this.resizeFrameId = raf(() => {
            this.setState({
              resizeStatus : RESIZE_STATUS_NONE
            });
          });
        });
      });
    });
  };

  componentWillUnmount() {
    raf.cancel(this.nextFrameActionId);
    raf.cancel(this.resizeFrameId);
  }

  renderTextArea = () => {
    const {autoSize, onResize, className, disabled} = this.props;
    const {textareaStyles, resizeStatus} = this.state;
    const otherProps = omit(this.props, [
      'prefixCls',
      'onPressEnter',
      'autoSize',
      'defaultValue',
      'allowClear',
      'onResize',
    ]);
    const prefixCls = 'textarea';
    const cls = cx(prefixCls, className, {
      [`${prefixCls}-disabled`]: disabled,
    });

    if ('value' in otherProps) {
      otherProps.value = otherProps.value || '';
    }

    const style = {
      ...this.props.style,
      ...textareaStyles,
      ...resizeStatus === RESIZE_STATUS_RESIZING ? {
        overflowX : 'hidden',
        overflowY : 'hidden'
      } : null
    };

    return (
      <ResizeObserver onResize={this.handleResize} disabled={!(autoSize || onResize)}>
        <textarea {...otherProps} className={cls} style={style} ref={this.saveTextArea} />
      </ResizeObserver>
    );
  };

  render() {
    return this.renderTextArea();
  }
}

export default ResizableTextArea;
